/**
 * What the create modal asks, and when it may stop asking.
 *
 * ── Two steps, one window (23 September 2026) ────────────────────────────────────────────────
 * „Program" asks where the plan STARTS — from nothing, or as a copy of a library template — and
 * „Szczegóły" asks the rest: the name, the category, the description, the cover and, from nothing,
 * the system. Canvas rows H and I, owner-approved. The window is `shared/ui/splitDialog` on both
 * steps and never changes size; what variant D withdrew on 21 September was a window that changed
 * WIDTH between steps and asked the system first, and neither comes back.
 *
 * ── A copy inherits its system and never chooses one ─────────────────────────────────────────
 * `self-paced-plan-requirements.md` R1 fixes a plan's mode at creation. A copy's creation IS the
 * template's tree, so its mode is the template's, and the details step draws it locked. There is
 * no setter path for it on a copy — `withMode` below refuses.
 *
 * ── Why the name is REQUIRED and not defaulted ───────────────────────────────────────────────
 * A default like "Nowy plan" is a name the trainer never chose and would have to delete, and it
 * survives into the library the moment they save — a shelf of identically-named plans is exactly
 * what the canon's "okładka jest tożsamością" note exists to prevent. The modal is four fields
 * long; asking for one of them is not a burden.
 *
 * ── The cover is now a CHOICE, and that reverses the 4 August note ──────────────────────────
 * "The cover is an outcome, not a question" was written when this app had no reader for the
 * server's cover pool, so the only thing a picker could have offered was a bundled image the save
 * could not express. `GET /plan-covers` is read as of 21 September 2026 and the 21.09 mockup draws
 * the row, so the owner rule's first half stands — every plan has a cover — and its second half
 * becomes a default rather than the only outcome: `null` means "let MoveBuddy choose from the
 * category", which is what every plan created before today carries.
 *
 * No React, no DOM, no query hook, no i18next: this is `model/`. The refusal leaves as a token.
 */

import {
  planCategoryKey,
  TEXT_LIMITS,
  type PlanCategoryKey,
  type PlanDocumentRead,
  type PlanSchedulingMode,
} from '~/entities/plan-draft';
import type { Translate } from '~/shared/lib/localeTranslate';

/** Everything the modal collects. Exactly the fields `createEmptyDraft` seeds a plan from. */
export interface CreatePlanChoices {
  readonly mode: PlanSchedulingMode;
  readonly category: PlanCategoryKey;
  /** Raw, as typed. Trimmed at the gate and at the seed, never in the field. */
  readonly name: string;
  /** Raw, as typed. Optional on the wire and optional here. */
  readonly description: string;
  /** A row from the category's pool, or `null` for "let MoveBuddy choose". */
  readonly coverId: number | null;
}

/**
 * The state the modal opens in.
 *
 * `date` because every seeded plan in this product is DateBased and it is the mode the canon tags
 * `Zalecane`; `strength` because it is the only category with a real cover pool and because the
 * canon draws it preselected. Neither is a guess the trainer cannot see: both are rendered as a
 * checked control they can change before anything is created.
 */
export const INITIAL_CHOICES: CreatePlanChoices = {
  mode: 'date',
  category: 'strength',
  name: '',
  description: '',
  coverId: null,
};

/** Why the modal cannot be completed, or `null` when it can. */
export type CreatePlanRefusal = 'name';

export function createPlanRefusal(choices: CreatePlanChoices): CreatePlanRefusal | null {
  return choices.name.trim() === '' ? 'name' : null;
}

/**
 * A category change drops a CURATED cover with it, and keeps the trainer's own.
 *
 * Owner rule, 4 August 2026: a cover belongs to its plan's category. The pool is read per category,
 * so a `coverId` chosen from Siła's pool is a row Cardio's pool does not contain — the save would
 * store it, the library would draw a barbell on a running plan, and nothing anywhere would say so.
 * `null` is the honest answer to "which cover from the new category", and it is also the default.
 *
 * An OWN upload was never covered by that rule and is kept: the backend stores it under a flat key
 * that fits every category, which is why it comes back in all six pools. The caller says which kind
 * is chosen — `model/coverPool`'s `coverSurvivesCategoryChange` — because this module cannot see
 * the pool.
 */
export function withCategory(
  choices: CreatePlanChoices,
  category: PlanCategoryKey,
  coverSurvives = false,
): CreatePlanChoices {
  if (category === choices.category) return choices;
  return coverSurvives
    ? { ...choices, category }
    : { ...choices, category, coverId: null };
}

/**
 * Change the plan's system — refused on a COPY, which inherits the template's (R1). The one writer
 * of `mode`, so the rule cannot be skipped by a second setter.
 */
export function withMode(
  choices: CreatePlanChoices,
  isCopy: boolean,
  mode: PlanSchedulingMode,
): CreatePlanChoices {
  return isCopy || mode === choices.mode ? choices : { ...choices, mode };
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Step 1 — where the plan starts.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** Which step the window is showing. */
export type CreatePlanStep = 'program' | 'details';

/** How the chosen template's full read stands. */
export type TemplateReadState =
  | { readonly status: 'pending' }
  | { readonly status: 'error' }
  | { readonly status: 'ready'; readonly read: PlanDocumentRead };

/**
 * Why „Dalej: szczegóły" cannot go on yet. Worded by the caller.
 *
 * The two refusals that are about the TEMPLATE rather than about the wait are the builder's own
 * (`readPlanDocument`): a tree this editor cannot hold cannot be copied into it either, because the
 * copy is opened in that editor and saved or sent whole from it.
 */
export type ProgramRefusal =
  | 'templatePending'
  | 'templateFailed'
  | 'templateSchedulingMode'
  | 'templateMultiSession';

/** `templateId` is where the plan starts: a library template's id, or `null` for from nothing. */
export function programRefusal(
  templateId: number | null,
  template: TemplateReadState,
): ProgramRefusal | null {
  if (templateId === null) return null;
  if (template.status === 'pending') return 'templatePending';
  if (template.status === 'error') return 'templateFailed';
  if (template.read.outcome === 'draft') return null;
  return template.read.refusal.reason === 'unsupportedSchedulingMode'
    ? 'templateSchedulingMode'
    : 'templateMultiSession';
}

// ─────────────────────────────────────────────────────────────────────────────────────────────
// Step 2 on a copy — the details, prefilled from the template.
// ─────────────────────────────────────────────────────────────────────────────────────────────

/** What a copy's details are read off — the template's own draft carries every one. */
export interface CopySource {
  readonly name: string;
  readonly description: string | null;
  /** The wire's category, verbatim. */
  readonly category: string | null;
  readonly coverId: number | null;
  readonly mode: PlanSchedulingMode;
}

/**
 * The first word of a client's name — what a copy made for them is named after.
 *
 * The roster carries one full-name string, so the first whitespace-separated token is the first
 * name for every client whose name is written first-name-first, which is how this product records
 * one. It is never declined: „· Maja", not a genitive nobody can derive.
 */
export function firstNameOf(fullName: string): string {
  return fullName.trim().split(/\s+/)[0] ?? '';
}

/**
 * The template's name with the copy's suffix, never past the wire's ceiling — and the SUFFIX is what
 * survives the cut, because it is the part that says this is a copy and for whom. A 200-character
 * template name is cut at its end, not the „(kopia)" after it.
 */
function copyName(templateName: string, suffix: string): string {
  const max = TEXT_LIMITS.planName.max;
  const room = max - suffix.length;
  if (room <= 0) return `${templateName.trim()}${suffix}`.slice(0, max);
  return `${templateName.trim().slice(0, room).trimEnd()}${suffix}`;
}

/**
 * The details a copy opens on: the template's own, under a name that says it is a copy.
 *
 * From the library the copy is „X (kopia)", a new program beside the template. For a client it is
 * „X · Maja": the plan is theirs, and the trainer's list of a client's plans reads by name. The
 * category falls to the dialog's own default only when the template carries one this build has no
 * key for — the details step draws it checked, so the trainer sees the answer before anything is
 * created.
 */
export function copyChoices(
  source: CopySource,
  recipient: { readonly clientName: string } | null,
  t: Translate,
): CreatePlanChoices {
  const suffix =
    recipient === null
      ? t('web:planCreate.copy.librarySuffix')
      : t('web:planCreate.copy.clientSuffix', { firstName: firstNameOf(recipient.clientName) });
  return {
    mode: source.mode,
    category: planCategoryKey(source.category) ?? INITIAL_CHOICES.category,
    name: copyName(source.name, suffix),
    description: source.description ?? '',
    coverId: source.coverId,
  };
}
